'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Phone,
  MapPin,
  CreditCard,
  UserPlus,
  AlertCircle,
} from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface RegisterFormData {
  nombre: string;
  apellido: string;
  cedula: string;
  telefono: string;
  ciudad: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function RegisterForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const passwordValue = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setErrorMsg('');

    const supabase = createClient();

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          nombre: data.nombre,
          apellido: data.apellido,
          cedula: data.cedula,
          telefono: data.telefono,
          ciudad: data.ciudad,
          full_name: `${data.nombre} ${data.apellido}`,
        },
      },
    });

    if (signUpError) {
      setErrorMsg(signUpError.message);
      setIsLoading(false);
      return;
    }

    // Sign in immediately after registration — no email verification required
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (signInError) {
      // Registration succeeded but auto-login failed; send user to login page
      setErrorMsg('Cuenta creada. Por favor inicia sesión.');
      setIsLoading(false);
      router.push('/login-screen');
      return;
    }

    // Update user_profile with extra fields (cedula, phone, ciudad)
    // The trigger creates the profile with basic info; we update the rest
    if (signInData?.user?.id) {
      await supabase
        .from('user_profiles')
        .update({
          cedula: data.cedula || '',
          phone: data.telefono || '',
          ciudad: data.ciudad || '',
          updated_at: new Date().toISOString(),
        })
        .eq('id', signInData.user.id);
    }

    router.push('/client-home');
  };

  // ── Registration form ───────────────────────────────────────────────────────
  return (
    <div className="card-elevated p-8 rounded-2xl">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Crear cuenta</h2>
        <p className="text-sm text-muted-foreground mt-1.5">
          Completa tus datos para registrarte en Lazos Leis
        </p>
      </div>

      {errorMsg && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
          <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{errorMsg}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Row: Nombre + Apellido */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Nombre */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="nombre" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <User size={14} className="text-primary" />
              Nombre
            </label>
            <input
              id="nombre"
              type="text"
              autoComplete="given-name"
              placeholder="Tu nombre"
              className="input-base"
              {...register('nombre', {
                required: 'El nombre es obligatorio',
                minLength: { value: 2, message: 'Mínimo 2 caracteres' },
              })}
            />
            {errors.nombre && (
              <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
                <AlertCircle size={12} />
                {errors.nombre.message}
              </p>
            )}
          </div>

          {/* Apellido */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="apellido" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <User size={14} className="text-primary" />
              Apellido
            </label>
            <input
              id="apellido"
              type="text"
              autoComplete="family-name"
              placeholder="Tu apellido"
              className="input-base"
              {...register('apellido', {
                required: 'El apellido es obligatorio',
                minLength: { value: 2, message: 'Mínimo 2 caracteres' },
              })}
            />
            {errors.apellido && (
              <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
                <AlertCircle size={12} />
                {errors.apellido.message}
              </p>
            )}
          </div>
        </div>

        {/* Row: Cédula + Teléfono */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Cédula */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cedula" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <CreditCard size={14} className="text-primary" />
              Cédula
            </label>
            <input
              id="cedula"
              type="text"
              placeholder="V-12345678"
              className="input-base"
              {...register('cedula', {
                required: 'La cédula es obligatoria',
                pattern: {
                  value: /^[VEve]-?\d{6,8}$/,
                  message: 'Formato: V-12345678',
                },
              })}
            />
            {errors.cedula && (
              <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
                <AlertCircle size={12} />
                {errors.cedula.message}
              </p>
            )}
          </div>

          {/* Teléfono */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="telefono" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <Phone size={14} className="text-primary" />
              Teléfono
            </label>
            <input
              id="telefono"
              type="tel"
              autoComplete="tel"
              placeholder="0412-1234567"
              className="input-base"
              {...register('telefono', {
                required: 'El teléfono es obligatorio',
                pattern: {
                  value: /^[\d\s\-\+\(\)]{7,15}$/,
                  message: 'Ingresa un teléfono válido',
                },
              })}
            />
            {errors.telefono && (
              <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
                <AlertCircle size={12} />
                {errors.telefono.message}
              </p>
            )}
          </div>
        </div>

        {/* Ciudad */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="ciudad" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <MapPin size={14} className="text-primary" />
            Ciudad
          </label>
          <input
            id="ciudad"
            type="text"
            autoComplete="address-level2"
            placeholder="Tu ciudad"
            className="input-base"
            {...register('ciudad', {
              required: 'La ciudad es obligatoria',
            })}
          />
          {errors.ciudad && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
              <AlertCircle size={12} />
              {errors.ciudad.message}
            </p>
          )}
        </div>

        {/* Correo electrónico */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <Mail size={14} className="text-primary" />
            Correo electrónico
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="tucorreo@ejemplo.com"
            className="input-base"
            {...register('email', {
              required: 'El correo es obligatorio',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Ingresa un correo válido',
              },
            })}
          />
          {errors.email && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
              <AlertCircle size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Contraseña */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="password" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <Lock size={14} className="text-primary" />
            Contraseña
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              className="input-base pr-11"
              {...register('password', {
                required: 'La contraseña es obligatoria',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Debe incluir mayúscula, minúscula y número',
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors p-0.5"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
              <AlertCircle size={12} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Confirmar contraseña */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="confirmPassword" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <Lock size={14} className="text-primary" />
            Confirmar contraseña
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repite tu contraseña"
              className="input-base pr-11"
              {...register('confirmPassword', {
                required: 'Confirma tu contraseña',
                validate: (value) =>
                  value === passwordValue || 'Las contraseñas no coinciden',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors p-0.5"
              aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
              <AlertCircle size={12} />
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full mt-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
              </svg>
              Creando cuenta...
            </>
          ) : (
            <>
              <UserPlus size={16} />
              Crear cuenta
            </>
          )}
        </button>
      </form>

      {/* Login link */}
      <p className="text-center text-sm text-muted-foreground mt-5">
        ¿Ya tienes una cuenta?{' '}
        <Link
          href="/login-screen"
          className="text-primary font-semibold hover:underline"
        >
          Inicia sesión
        </Link>
      </p>
    </div>
  );
}
